/*src/pages/BlockedPage.tsx*/
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, CreditCard } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { api, crmService } from '../services/crmApi';
import { getVisibleBillingPayments, type BillingPaymentLike } from '../utils/billingPayments';

const PENDING_PAYMENT_NOTICE_KEY = '@VendaPro:PendingPaymentNotice';

const BlockedPage: React.FC = () => {
  const { user, signOut, updateUser } = useAuth();
  const [payments, setPayments] = useState<BillingPaymentLike[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const companyStatus = user?.companyStatus || user?.company?.status;
  const isPendingPayment = companyStatus === 'pending_payment';

  useEffect(() => {
    let mounted = true;

    const loadPendingPayments = async () => {
      if (!isPendingPayment || !user?.asaasCustomerId) {
        if (mounted) {
          setPayments([]);
          setLoadingPayments(false);
        }
        return;
      }

      try {
        setLoadingPayments(true);
        const response = await crmService.getAsaasPayments(user.asaasCustomerId);
        if (!mounted) return;
        setPayments(Array.isArray(response?.data) ? response.data : []);
      } catch {
        if (!mounted) return;
        setPayments([]);
      } finally {
        if (mounted) {
          setLoadingPayments(false);
        }
      }
    };

    loadPendingPayments();

    return () => {
      mounted = false;
    };
  }, [isPendingPayment, user?.asaasCustomerId]);

  useEffect(() => {
    if (isPendingPayment) {
      sessionStorage.setItem(PENDING_PAYMENT_NOTICE_KEY, '1');
      localStorage.removeItem(PENDING_PAYMENT_NOTICE_KEY);
    } else {
      sessionStorage.removeItem(PENDING_PAYMENT_NOTICE_KEY);
      localStorage.removeItem(PENDING_PAYMENT_NOTICE_KEY);
    }
  }, [isPendingPayment]);

  const reloadPendingPayments = async () => {
    if (!user?.asaasCustomerId) {
      setPayments([]);
      return;
    }

    const response = await crmService.getAsaasPayments(user.asaasCustomerId);
    setPayments(Array.isArray(response?.data) ? response.data : []);
  };

  const { actionablePayments: payablePayments, hiddenUpcomingCount } = useMemo(
    () => getVisibleBillingPayments(payments),
    [payments]
  );

  const formatDate = (dateValue?: string) => {
    if (!dateValue) return '--';
    const [year, month, day] = String(dateValue).split('-');
    if (!year || !month || !day) return dateValue;
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));

  const handleVerifyPayment = async () => {
    if (!isPendingPayment) {
      window.location.reload();
      return;
    }

    try {
      setCheckingPayment(true);

      const profileRes = await api.get('/api/auth/me');
      const backendUser = profileRes.data;
      const nextCompanyStatus = backendUser.companyStatus || backendUser.company?.status || 'active';

      updateUser({
        companyStatus: nextCompanyStatus,
        planId: backendUser.planId,
        invoiceUrl: backendUser.invoiceUrl,
        asaasCustomerId: backendUser.asaasCustomerId,
        company: backendUser.company
      });

      if (nextCompanyStatus === 'active') {
        sessionStorage.removeItem(PENDING_PAYMENT_NOTICE_KEY);
        localStorage.removeItem(PENDING_PAYMENT_NOTICE_KEY);
        return;
      }

      await reloadPendingPayments();
    } catch {
      await reloadPendingPayments().catch(() => {});
    } finally {
      setCheckingPayment(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-800 text-center p-5">
      <div className="text-6xl mb-4">{isPendingPayment ? '💳' : '🚫'}</div>
      <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
        {isPendingPayment ? 'Regularize Sua Assinatura' : 'Acesso Suspenso'}
      </h1>
      <p className="text-slate-600 dark:text-slate-400 dark:text-slate-500 mt-2 max-w-md">
        {isPendingPayment
          ? 'Seu pagamento está em atraso. Seu acesso completo fica pausado temporariamente, mas você pode regularizar agora mesmo sem falar com o suporte.'
          : 'Identificamos uma pendência na sua assinatura. Para continuar utilizando o sistema, por favor regularize o pagamento.'}
      </p>

      {isPendingPayment && (
        <div className="mt-4 px-4 py-3 rounded-2xl bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/40 text-blue-800 dark:text-blue-200 max-w-2xl text-sm font-medium">
          A liberação costuma acontecer automaticamente em até 5 minutos após a confirmação do pagamento pelo Asaas.
        </div>
      )}

      {isPendingPayment && (
        <div className="mt-8 w-full max-w-3xl bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 text-left">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-2xl bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
              <CreditCard size={22} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">Faturas pendentes</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">Pague uma fatura abaixo para liberar novamente o sistema.</p>
            </div>
          </div>

          {hiddenUpcomingCount > 0 && (
            <div className="mb-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-3 text-xs text-slate-600 dark:text-slate-400">
              As próximas cobranças da assinatura ficam ocultas aqui para evitar pagamento fora do mês atual.
            </div>
          )}

          {loadingPayments ? (
            <div className="py-10 flex items-center justify-center">
              <Loader2 size={28} className="animate-spin text-blue-600 dark:text-blue-400" />
            </div>
          ) : payablePayments.length === 0 ? (
            <div className="py-8 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-center text-sm text-slate-500 dark:text-slate-400">
              Nenhuma fatura pendente foi encontrada no momento. Se o atraso acabou de ocorrer, aguarde alguns instantes e tente novamente.
            </div>
          ) : (
            <div className="space-y-3">
              {payablePayments.map((payment) => {
                const paymentLink = payment?.bankSlipUrl || payment?.invoiceUrl || payment?.paymentLink;
                const isOverdue = payment?.status === 'OVERDUE';

                return (
                  <div
                    key={payment.id}
                    className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-slate-800 dark:text-slate-200">
                          {payment.description || 'Assinatura CRM'}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${isOverdue ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>
                          {isOverdue ? 'Atrasada' : 'Pendente'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Vencimento: {formatDate(payment?.dueDate)}
                      </p>
                      <p className="text-base font-black text-slate-900 dark:text-slate-100">
                        {formatCurrency(payment?.value)}
                      </p>
                    </div>

                    {paymentLink ? (
                      <a
                        href={paymentLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition-colors"
                      >
                        Pagar Agora
                      </a>
                    ) : (
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        Link de pagamento indisponível
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="mt-8 flex gap-4">
        <button
          onClick={handleVerifyPayment}
          disabled={checkingPayment}
          className="px-5 py-2.5 bg-slate-900 hover:bg-black text-white rounded-xl text-sm font-bold transition-colors disabled:opacity-60 inline-flex items-center gap-2"
        >
          {checkingPayment ? <Loader2 size={16} className="animate-spin" /> : null}
          {isPendingPayment ? 'Já paguei, verificar novamente' : 'Tentar novamente'}
        </button>

        {!isPendingPayment && user?.invoiceUrl && (
          <a
            href={user.invoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Regularizar Agora
          </a>
        )}
      </div>

      <button
        onClick={signOut}
        className="mt-8 text-sm text-red-500 hover:text-red-600 underline"
      >
        Sair do sistema
      </button>
    </div>
  );
};

export default BlockedPage;
